import { useLayoutEffect } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { nanoid } from 'nanoid';

function DrillDown({rootId= nanoid()}) {
  useLayoutEffect(() => {
    let root = am5.Root.new(rootId);

    root.setThemes([
      am5themes_Animated.new(root)
    ]);

    let container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.p100,
        height: am5.p100,
        layout: root.horizontalLayout
      })
    );

    // Column Chart
    let columnChart = container.children.push(am5xy.XYChart.new(root, {
      width: am5.p50,
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      layout: root.verticalLayout
    }));

    let yRenderer = am5xy.AxisRendererY.new(root, {});
    let yAxis = columnChart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "category",
      renderer: yRenderer
    }));

    yRenderer.grid.template.setAll({ location: 1 });

    let xAxis = columnChart.xAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererX.new(root, { strokeOpacity: 0.1 })
    }));

    let columnSeries = columnChart.series.push(am5xy.ColumnSeries.new(root, {
      name: "Column Series",
      xAxis: xAxis,
      yAxis: yAxis,
      valueXField: "value",
      categoryYField: "category"
    }));

    columnSeries.columns.template.setAll({ tooltipText: "{categoryY}: {valueX}" });

    // Pie Chart
    let pieChart = container.children.push(
      am5percent.PieChart.new(root, {
        width: am5.p50,
        innerRadius: am5.percent(50)
      })
    );

    let pieSeries = pieChart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category"
      })
    );

    pieSeries.slices.template.setAll({
      templateField: "sliceSettings",
      strokeOpacity: 0
    });

    let label1 = pieChart.seriesContainer.children.push(am5.Label.new(root, {
      text: "",
      fontSize: 35,
      fontWeight: "bold",
      centerX: am5.p50,
      centerY: am5.p50
    }));

    let label2 = pieChart.seriesContainer.children.push(am5.Label.new(root, {
      text: "",
      fontSize: 12,
      centerX: am5.p50,
      centerY: am5.p50,
      dy: 30
    }));

    let currentSlice;

    pieSeries.slices.template.on("active", function(active, slice) {
      if (currentSlice && currentSlice != slice && active) {
        currentSlice.set("active", false);
      }

      let color = slice.get("fill");

      label1.setAll({
        fill: color,
        text: root.numberFormatter.format(slice.dataItem.get("valuePercentTotal"), "#.'%'")
      });

      label2.set("text", slice.dataItem.get("category"));

      columnSeries.columns.template.setAll({
        fill: slice.get("fill"),
        stroke: slice.get("fill")
      });

      columnSeries.data.setAll(slice.dataItem.dataContext.breakdown);
      yAxis.data.setAll(slice.dataItem.dataContext.breakdown);

      currentSlice = slice;
    });

    pieSeries.labels.template.set("forceHidden", true);
    pieSeries.ticks.template.set("forceHidden", true);

    // Set up data
    let data = [
      {
        category: "Critical",
        value: 89,
        sliceSettings: {
          fill: am5.color(0xdc4534)
        },
        breakdown: [
          { category: "Sales inquiries", value: 29 },
          { category: "Support requests", value: 40 },
          { category: "Bug reports", value: 11 },
          { category: "Other", value: 9 }
        ]
      },
      {
        category: "Acceptable",
        value: 71,
        sliceSettings: {
          fill: am5.color(0xd7a700)
        },
        breakdown: [
          { category: "Sales inquiries", value: 22 },
          { category: "Support requests", value: 30 },
          { category: "Bug reports", value: 11 },
          { category: "Other", value: 10 }
        ]
      },
      {
        category: "Good",
        value: 120,
        sliceSettings: {
          fill: am5.color(0x68ad5c)
        },
        breakdown: [
          { category: "Sales inquiries", value: 60 },
          { category: "Support requests", value: 35 },
          { category: "Bug reports", value: 15 },
          { category: "Other", value: 10 }
        ]
      }
    ];

    pieSeries.data.setAll(data);

    pieSeries.events.on("datavalidated", function() {
      pieSeries.slices.getIndex(0).set("active", true);
    });

    return () => {
      root.dispose();
    };
  }, []);

  return (
    <div id={rootId} className="h-[600px] w-full"></div>
  );
}

export { DrillDown };
